import { useState, type ReactNode } from 'react';
import { CallBadge, type CallRun } from './CallBadge';
import { StepPromptNav } from './StepPromptNav';
import { type TipPicture } from './PromptBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// ONE BADGE OVER SEVERAL CALLS: a press that makes many — one per block of pages, one per
// listing — and a badge that walks them with the stepper every many-call badge in the app uses
// (StepPromptNav), each entry its own question and its own answer.
//
// What the other many-call badges write out by hand (a walk index, the nav, the entry on
// screen), held once here for a caller that has nothing to add to it but its list.
export type CallEntry = {
  // Stable per call: it is the badge's `revision`, so stepping refetches the question.
  key: string;
  // What the stepper names it, and the line under that.
  label: string;
  sub?: string;
  // What this call would ask — the words, its pictures, the model it goes to.
  load: () => Promise<{ prompt: string; urls?: (string | TipPicture)[]; model?: string }>;
  // And what it answered last time, where it has.
  answer?: ReactNode;
};

export function CallsBadge({ title, entries, run, named }: {
  title: string;
  // Its title on the badge itself — see CallBadge.
  named?: boolean;
  entries: readonly CallEntry[];
  run?: CallRun;
}) {
  const { t } = useLanguage();
  const [walk, setWalk] = useState(0);
  // Clamped rather than reset: a list that shrinks under the open panel (a link removed)
  // leaves it on the last entry instead of on nothing.
  const at = Math.min(walk, Math.max(entries.length - 1, 0));
  const shown = entries[at];
  if (!shown) return null;
  return (
    <CallBadge
      title={title}
      named={named}
      run={run}
      revision={shown.key}
      promptNav={(
        <StepPromptNav
          steps={entries.map((_, i) => i + 1)}
          labelOf={(i) => entries[i].label}
          subOf={(i) => entries[i].sub}
          at={at}
          onGo={setWalk}
        />
      )}
      promptLabel={shown.sub ? `${shown.label} · ${shown.sub}` : shown.label}
      load={shown.load}
      answer={shown.answer ? { label: t('notice.cat.reasoningTitle'), content: shown.answer } : undefined}
    />
  );
}
